import { useEffect } from "react";
import type { EvidenceRecord, FindingView } from "../../api/types.js";

type ValidationEvent = FindingView["validations"][number];

import { findingDecisionSnapshot } from "./FindingInspector.js";
import { codeBehaviorNarrative, functionalImpactNarrative, impactNarrative } from "./narrative.js";

export interface FindingStoryCopy {
  readonly title: string;
  readonly ruleId: string;
  readonly action: string;
  readonly stateExplanation: string;
  readonly behavior: { readonly file: string; readonly snippet: string };
  readonly impact: readonly string[];
  readonly functionalImpact: readonly string[];
  readonly redactedSnippet: string;
  readonly secretEvidenceRedacted: boolean;
}

export interface FindingProofCopy {
  readonly replayStatus: EvidenceRecord["replayStatus"];
  readonly snapshotId: string;
  readonly fileSha256: string;
  readonly snippetSha256: string;
  readonly evidenceSha256: string;
  readonly path: string;
  readonly lineStart: number;
  readonly lineEnd: number;
  readonly configurationHash: string | null;
}

export interface FindingHistoryCopy {
  readonly id: string;
  readonly stage: ValidationEvent["stage"];
  readonly actorType: ValidationEvent["actorType"];
  readonly actorId: string;
  readonly validatorId: string;
  readonly outcome: ValidationEvent["outcome"];
  readonly rationale: string;
  readonly contradictions: readonly string[];
  readonly createdAt: string;
}

export interface FindingStoryModel {
  readonly story: FindingStoryCopy;
  readonly proof: FindingProofCopy;
  readonly history: readonly FindingHistoryCopy[];
}

export function findingStoryModel(finding: FindingView): FindingStoryModel {
  const snapshot = findingDecisionSnapshot(finding);
  return {
    story: {
      title: finding.title,
      ruleId: finding.ruleId,
      action: snapshot.action,
      stateExplanation: snapshot.stateExplanation,
      behavior: codeBehaviorNarrative(finding),
      impact: impactNarrative(finding),
      functionalImpact: functionalImpactNarrative(finding),
      redactedSnippet: finding.evidence.redactedSnippet,
      secretEvidenceRedacted: Boolean(finding.evidence.secretFingerprint),
    },
    proof: {
      replayStatus: finding.evidence.replayStatus,
      snapshotId: finding.snapshotId,
      fileSha256: finding.evidence.fileSha256,
      snippetSha256: finding.evidence.snippetSha256,
      evidenceSha256: finding.evidence.evidenceSha256,
      path: finding.location.path,
      lineStart: finding.location.lineStart,
      lineEnd: finding.location.lineEnd,
      configurationHash: finding.sourceMetadata?.configurationHash ?? null,
    },
    history: finding.validations.map((event) => ({
      id: event.id,
      stage: event.stage,
      actorType: event.actorType,
      actorId: event.actorId,
      validatorId: event.validatorId,
      outcome: event.outcome,
      rationale: event.rationale,
      contradictions: event.contradictions,
      createdAt: event.createdAt,
    })),
  };
}

export function cycleFindingId(
  ids: string[],
  current: string | null,
  delta: 1 | -1,
): string | null {
  if (ids.length === 0) return null;
  const index = current == null ? -1 : ids.indexOf(current);
  if (index === -1) {
    return delta === 1 ? (ids[0] ?? null) : (ids[ids.length - 1] ?? null);
  }
  const next = (index + delta + ids.length) % ids.length;
  return ids[next] ?? null;
}

export function isEditableKeyboardTarget(
  target: EventTarget | null | { tagName?: string; isContentEditable?: boolean },
): boolean {
  if (target == null || typeof target !== "object") return false;
  const element = target as { tagName?: string; isContentEditable?: boolean };
  if (element.isContentEditable) return true;
  const tag = element.tagName?.toUpperCase();
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export function resolveFindingQueueKey(input: {
  key: string;
  ids: readonly string[];
  current: string | null;
  open: boolean;
  typing: boolean;
}): { id: string } | null {
  if (!input.open || input.typing) return null;
  const ids = [...input.ids];
  if (input.key === "j" || input.key === "J" || input.key === "ArrowDown") {
    const id = cycleFindingId(ids, input.current, 1);
    return id ? { id } : null;
  }
  if (input.key === "k" || input.key === "K" || input.key === "ArrowUp") {
    const id = cycleFindingId(ids, input.current, -1);
    return id ? { id } : null;
  }
  if ((input.key === "Enter" || input.key === "o" || input.key === "O") && input.current) {
    return { id: input.current };
  }
  return null;
}

export type FindingListShortcut =
  | { type: "focus"; id: string }
  | { type: "open"; id: string }
  | { type: "close" }
  | { type: "select"; id: string }
  | { type: "next-page" }
  | { type: "prev-page" }
  | { type: "actionable" }
  | { type: "all" }
  | { type: "filters" }
  | { type: "copy-link" }
  | { type: "refresh" };

export function resolveFindingListShortcut(input: {
  key: string;
  shiftKey?: boolean;
  ids: readonly string[];
  focusedId: string | null;
  open: boolean;
  typing: boolean;
  defaultPrevented?: boolean;
}): FindingListShortcut | null {
  if (input.typing || input.defaultPrevented) return null;
  const ids = [...input.ids];
  const focusedId = input.focusedId ?? ids[0] ?? null;

  if (input.open) {
    if (input.key === "Escape") return { type: "close" };
    return null;
  }

  if (input.key === "j" || input.key === "J" || input.key === "ArrowDown") {
    const id = cycleFindingId(ids, input.focusedId, 1);
    return id ? { type: "focus", id } : null;
  }
  if (input.key === "k" || input.key === "K" || input.key === "ArrowUp") {
    const id = cycleFindingId(ids, input.focusedId, -1);
    return id ? { type: "focus", id } : null;
  }
  if ((input.key === "Enter" || input.key === "o" || input.key === "O") && focusedId) {
    return { type: "open", id: focusedId };
  }
  if (input.key === "Escape") return { type: "close" };
  if ((input.key === "x" || input.key === "X") && focusedId)
    return { type: "select", id: focusedId };
  if ((input.key === "e" || input.key === "E") && focusedId) return { type: "open", id: focusedId };
  if (input.key === "n" || input.key === "N") return { type: "next-page" };
  if (input.key === "p" || input.key === "P") return { type: "prev-page" };
  if (input.key === "a" && !input.shiftKey) return { type: "actionable" };
  if (input.key === "A" && input.shiftKey) return { type: "all" };
  if (input.key === "f" || input.key === "F") return { type: "filters" };
  if (input.key === "c" || input.key === "C") return { type: "copy-link" };
  if (input.key === "r" || input.key === "R") return { type: "refresh" };
  return null;
}

export function useFindingQueueKeys({
  ids,
  current,
  open,
  onSelect,
}: {
  ids: readonly string[];
  current: string | null;
  open: boolean;
  onSelect: (id: string) => void;
}): void {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const resolved = resolveFindingQueueKey({
        key: event.key,
        ids,
        current,
        open: true,
        typing: isEditableKeyboardTarget(event.target),
      });
      if (!resolved) return;
      event.preventDefault();
      onSelect(resolved.id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current, ids, onSelect, open]);
}
